import { useCallback, useEffect, useMemo, useState } from "react";
import { Head, router } from "@inertiajs/react";
import { Wand2 } from "lucide-react";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/Button";
import {
  plan_bottom_up_by_day_channel_path,
  plan_bottom_up_by_day_index_path,
} from "@/lib/routes";
import { useMarket } from "@/lib/useMarket";
import type { PlanSwitcherData } from "../TopDown/components/PlanSwitcher";
import PlanHeader from "../components/PlanHeader";
import PlanNavRow from "../components/PlanNavRow";
import ByDayToolbar from "./components/ByDayToolbar";
import StickyBar from "./components/StickyBar";
import BottomUpDailyGrid, { type DailyRow } from "./components/BottomUpDailyGrid";
import type { ScopeNode } from "@/components/ChannelMenu";
import { FlashAlert } from "@/components/Flash/FlashAlert";
import PromoDayBar from "./components/PromoDayBar";
import ImportDaysDialog, { compactDate } from "./components/ImportDaysDialog";
import { useSaveStatus } from "./utils/useSaveStatus";
import { useCellSaver } from "./utils/useCellSaver";
import { distributeRemaining } from "./utils/distributeRemaining";
import { useDayWindow } from "./utils/useDayWindow";
import { dayUnitPrice } from "./utils/dayUnitPrice";
import { matchesProductSearch } from "./utils/productSearch";

interface Channel {
  id: string;
  name: string;
  slug: string;
}

interface PriceLevel {
  id: string;
  name: string;
  color: string | null;
}

interface Props {
  plan: {
    id: string;
    name: string;
    slug: string;
    begin_date: string;
    end_date: string;
    currency: string | null;
    revenue_target: number;
    org_node: { id: string; name: string };
  };
  version: { id: string; version_number: number };
  months: string[];
  selected_month: string;
  days: string[];
  rows: DailyRow[];
  plan_node_order: string[];
  scope_tree: ScopeNode[];
  current_channel: Channel;
  price_levels: PriceLevel[];
  pricing_by_date: Record<string, string>;
  pricing_labels_by_date: Record<string, string>;
  promo_labels: string[];
  plan_switcher: PlanSwitcherData;
  can: { edit: boolean; manage_pricing: boolean };
}

// Temporarily hidden pending rollout decision; per-row distribute stays live.
const SHOW_DISTRIBUTE_ALL = false;

export default function ByDay({
  plan,
  version,
  months,
  selected_month,
  days,
  rows,
  plan_node_order,
  scope_tree,
  current_channel,
  price_levels,
  pricing_by_date,
  pricing_labels_by_date,
  promo_labels,
  plan_switcher,
  can,
}: Props) {
  const market = useMarket();
  const [localRows, setLocalRows] = useState<DailyRow[]>(rows);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [lastClickedDate, setLastClickedDate] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [toolbarHeight, setToolbarHeight] = useState(0);
  const { statusFor, markSaved, markError, dismissError, errorMessage } = useSaveStatus();
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);

  // Server refreshes (CSV import, promo writes) come back as a new rows prop
  // while this component stays mounted — re-seed the optimistic copy so the
  // grid reflects them without a manual reload.
  useEffect(() => setLocalRows(rows), [rows]);

  // Today onward: the only days that can be typed into, filled, or marked as
  // promo. The whole month (elapsed days included) is drawn and exported.
  const editableDays = useMemo(() => days.filter((d) => d >= todayIso), [days, todayIso]);

  const visibleRows = useMemo(
    () => localRows.filter((r) => matchesProductSearch(r, searchQuery)),
    [localRows, searchQuery],
  );

  const goToMonth = useCallback(
    (iso: string, pos?: "end") => {
      router.get(
        plan_bottom_up_by_day_channel_path(market.slug, plan.slug, current_channel.slug, {
          month: iso.slice(0, 7),
          ...(pos ? { pos } : {}),
        }),
        {},
        { preserveState: false },
      );
    },
    [market.slug, plan.slug, current_channel.slug],
  );

  const hrefForChannel = useCallback(
    (slug: string) => plan_bottom_up_by_day_channel_path(market.slug, plan.slug, slug, { month: selected_month.slice(0, 7) }),
    [market.slug, plan.slug, selected_month],
  );

  const visibleMonths = useMemo(() => {
    const currentMonth = new Date().toISOString().slice(0, 7);
    return months.filter((m) => m >= currentMonth);
  }, [months]);

  const usedPriceLevels = useMemo(() => {
    const ids = new Set<string>();
    days.forEach((d) => {
      const id = pricing_by_date[d];
      if (id) ids.add(id);
    });
    return price_levels.filter((pl) => ids.has(pl.id));
  }, [price_levels, pricing_by_date, days]);

  const { visibleDays, setDayCount, ...stepper } = useDayWindow({
    days,
    todayIso,
    visibleMonths,
    selectedMonth: selected_month,
    goToMonth,
  });

  const { saveCells, handlePasteCells, handleCellChange } = useCellSaver({
    marketSlug: market.slug,
    planSlug: plan.slug,
    channelId: current_channel.id,
    setLocalRows,
    markSaved,
    markError,
  });

  // Promo days = dates carrying a price level. Distribution fills the rest of
  // the editable window (today onward); elapsed days keep their quantities.
  const fillDates = useMemo(
    () => editableDays.filter((d) => !pricing_by_date[d]),
    [editableDays, pricing_by_date],
  );

  const handleToggleDate = useCallback(
    (date: string, shiftKey: boolean) => {
      if (date < todayIso) return;
      if (shiftKey && lastClickedDate) {
        const [lo, hi] = [lastClickedDate, date].sort();
        const range = editableDays.filter((d) => d >= lo && d <= hi);
        setSelectedDates((prev) => Array.from(new Set([...prev, ...range])));
      } else {
        setSelectedDates((prev) =>
          prev.includes(date) ? prev.filter((d) => d !== date) : [...prev, date],
        );
      }
      setLastClickedDate(date);
    },
    [lastClickedDate, editableDays, todayIso],
  );

  // Additive: each drag unions its range into the selection, so several
  // separate ranges can be built up. Click toggles days off; Cancel clears.
  const levelNameById = useMemo(() => new Map(price_levels.map((pl) => [pl.id, pl.name])), [price_levels]);

  // Matrix CSV of the whole viewed month: promotion_day row first (pre-filled
  // with each day's pricing type), product_id/short_name/type/DD/MM date
  // header, then three rows per product tagged by the `type` column: qty,
  // price (the unit price the grid uses that day), revenue (qty × price).
  // Round-trips through the importer, which reads qty rows only (blank = leave
  // untouched; elapsed-day columns are ignored on import; short_name is
  // read-only reference, ignored on import).
  const exportCsv = useCallback(() => {
    const promoRow = ["promotion_day", "", "", ...days.map((d) => levelNameById.get(pricing_by_date[d]) ?? "")].join(",");
    const header = ["product_id", "short_name", "type", ...days.map(compactDate)].join(",");
    const lines = localRows.flatMap((r) => {
      const byDate = new Map(r.days.map((c) => [c.period_date, c.quantity]));
      const shortName = r.product_short_name ?? "";
      const qty = days.map((d) => byDate.get(d) || "");
      const price = days.map((d) => dayUnitPrice(r.prices_by_level, r.asp?.price, pricing_by_date[d])?.price ?? "");
      const revenue = days.map((d, i) => (qty[i] !== "" && price[i] !== "" ? Number(qty[i]) * Number(price[i]) : ""));
      return [
        [r.product_external_id, shortName, "qty", ...qty].join(","),
        [r.product_external_id, shortName, "price", ...price].join(","),
        [r.product_external_id, shortName, "revenue", ...revenue].join(","),
      ];
    });
    const blob = new Blob([[promoRow, header, ...lines].join("\n") + "\n"], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `by-day-${plan.slug}-${current_channel.slug}-${selected_month.slice(0, 7)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [levelNameById, days, pricing_by_date, localRows, plan.slug, current_channel.slug, selected_month]);

  const handleDragSelect = useCallback(
    (dates: string[]) => {
      const editable = dates.filter((d) => d >= todayIso);
      if (editable.length === 0) return;
      setSelectedDates((prev) => Array.from(new Set([...prev, ...editable])));
      setLastClickedDate(editable[editable.length - 1] ?? null);
    },
    [todayIso],
  );

  const distributeUpdatesFor = useCallback(
    (row: DailyRow) =>
      distributeRemaining(row.month_target, row.days, fillDates).map((u) => ({
        product_external_id: row.product_external_id,
        period_date: u.period_date,
        quantity: u.quantity,
        manual: false,
      })),
    [fillDates],
  );

  const distributeRow = useCallback(
    (row: DailyRow) => saveCells(distributeUpdatesFor(row), { immediate: true }),
    [distributeUpdatesFor, saveCells],
  );

  const distributeAll = useCallback(
    () => saveCells(visibleRows.flatMap(distributeUpdatesFor), { immediate: true }),
    [visibleRows, distributeUpdatesFor, saveCells],
  );

  // Zero out every product's quantity on the given dates (all rows, not just
  // the search-filtered ones) and drop their manual flags.
  const clearQuantities = useCallback(
    (dates: string[]) => {
      saveCells(
        localRows.flatMap((row) =>
          dates.map((d) => ({
            product_external_id: row.product_external_id,
            period_date: d,
            quantity: 0,
            manual: false,
          })),
        ),
        { immediate: true },
      );
      setSelectedDates([]);
    },
    [localRows, saveCells],
  );

  return (
    <AppLayout
      flush
      header={
        <PlanHeader
          name={plan.name}
          orgNodeName={plan.org_node.name}
          revenueTarget={plan.revenue_target}
          currency={plan.currency}
          beginDate={plan.begin_date}
          endDate={plan.end_date}
        />
      }
    >
      <Head title={`Daily Planning — ${plan.name}`} />
      <div>
        <PlanNavRow
          planSwitcher={plan_switcher}
          redirectPath={plan_bottom_up_by_day_index_path(market.slug)}
          className="mx-6 mt-5 mb-4"
        />
        <div
          style={{ background: "var(--background)", borderColor: "var(--border)" }}
          className="border-y"
        >
          {/* One sticky slot: the promo bar swaps in for the toolbar row
              while days are selected, so the grid never shifts. */}
          <StickyBar onHeightChange={setToolbarHeight} className="px-0 pt-0">
            {can.manage_pricing && selectedDates.length > 0 ? (
              <PromoDayBar
                planSlug={plan.slug}
                channelSlug={current_channel.slug}
                selectedDates={selectedDates}
                levels={price_levels}
                suggestedLabels={promo_labels}
                pricingByDate={pricing_by_date}
                onClearQuantities={clearQuantities}
                onClose={() => setSelectedDates([])}
              />
            ) : (
              <ByDayToolbar
                scopeTree={scope_tree}
                currentNodeId={current_channel.id}
                hrefForChannel={hrefForChannel}
                stepper={stepper}
                usedPriceLevels={usedPriceLevels}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                canImport={can.edit}
                onOpenImport={() => setImportOpen(true)}
                onExport={exportCsv}
                distributeAll={
                  SHOW_DISTRIBUTE_ALL && can.edit ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 gap-1.5 text-xs"
                      disabled={fillDates.length === 0}
                      onClick={distributeAll}
                      title="Fill non-promo, non-manual days evenly with the remaining monthly target"
                    >
                      <Wand2 className="h-3.5 w-3.5" />
                      Distribute all
                    </Button>
                  ) : null
                }
              />
            )}
          </StickyBar>

          <BottomUpDailyGrid
            stickyOffset={toolbarHeight}
            days={visibleDays}
            rows={visibleRows}
            planCurrency={plan.currency}
            editable={can.edit}
            lockedBefore={todayIso}
            onCellChange={handleCellChange}
            onPasteCells={handlePasteCells}
            priceLevels={price_levels}
            pricingByDate={pricing_by_date}
            pricingLabelsByDate={pricing_labels_by_date}
            planNodeOrder={plan_node_order}
            saveStatusFor={statusFor}
            onDayCountChange={setDayCount}
            selectedDates={can.manage_pricing ? new Set(selectedDates) : undefined}
            onToggleDate={can.manage_pricing ? handleToggleDate : undefined}
            onDragSelect={can.manage_pricing ? handleDragSelect : undefined}
            onDistributeRow={can.edit ? distributeRow : undefined}
          />
        </div>
      </div>

      <ImportDaysDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        slug={plan.slug}
        channelSlug={current_channel.slug}
        channelName={current_channel.name}
        levelNames={price_levels.map((pl) => pl.name)}
        days={editableDays}
        pricingByDate={pricing_by_date}
        levelNameById={levelNameById}
        productIds={localRows.map((r) => r.product_external_id)}
        selectedMonth={selected_month}
      />

      {errorMessage && (
        <FlashAlert message={errorMessage} onDismiss={dismissError} />
      )}
    </AppLayout>
  );
}
